import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { LoadingSpinner } from '../../../components/shared/LoadingSpinner';
import { CloseIcon, ExpandMoreIcon, EditIcon } from '../../../theme/icons';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';

const ScanScheduleModal = ({
  open,
  onClose,
  orgCode,
  accountCode,
  accountNumber,
  accountName,
  scanSchedules,
  isGettingScanSchedules,
  getScanSchedulesError,
  getScanSchedulesMessage,
  getScanSchedules,
  updateScanScheduleFrequency,
  isUpdatingScanSchedule,
  updateScanScheduleError,
  updateScanScheduleMessage,
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const [editingStatusRows, setEditingStatusRows] = useState({});
  const [editValues, setEditValues] = useState({});
  const [editStatusValues, setEditStatusValues] = useState({});

  useEffect(() => {
    if (open && orgCode && accountCode) {
      getScanSchedules(orgCode, accountCode);
    } else if (!open) {
      // Clear local state when modal closes
      setExpandedRows({});
      setEditingRows({});
      setEditingStatusRows({});
      setEditValues({});
      setEditStatusValues({});
    }
  }, [open, orgCode, accountCode, getScanSchedules]);

  // Refresh schedules after successful update
  useEffect(() => {
    if (updateScanScheduleMessage && !isUpdatingScanSchedule && orgCode && accountCode) {
      // Refresh the schedules list to get the latest data from server
      getScanSchedules(orgCode, accountCode);
    }
  }, [updateScanScheduleMessage, isUpdatingScanSchedule, orgCode, accountCode, getScanSchedules]);

  const toggleRowExpansion = (scheduleId) => {
    setExpandedRows(prev => ({
      ...prev,
      [scheduleId]: !prev[scheduleId]
    }));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "paused":
        return "warning";
      case "inactive":
        return "error";
      default:
        return "default";
    }
  };

  const getScheduleTypeColor = (scheduleType) => {
    switch (scheduleType?.toLowerCase()) {
      case "recurring":
        return "primary";
      case "on_demand":
        return "secondary";
      default:
        return "default";
    }
  };

  const handleEditClick = (scheduleId, currentFrequency) => {
    setEditingRows(prev => ({ ...prev, [scheduleId]: true }));
    setEditValues(prev => ({ ...prev, [scheduleId]: currentFrequency || "" }));
  };

  const handleEditStatusClick = (scheduleId, currentStatus) => {
    setEditingStatusRows(prev => ({ ...prev, [scheduleId]: true }));
    // Map "paused" to "pause" for UI display
    const displayStatus = currentStatus === "paused" ? "pause" : currentStatus;
    setEditStatusValues(prev => ({ ...prev, [scheduleId]: displayStatus || "" }));
  };

  const handleCancelEdit = (scheduleId) => {
    setEditingRows(prev => ({ ...prev, [scheduleId]: false }));
    setEditValues(prev => ({ ...prev, [scheduleId]: undefined }));
  };

  const handleCancelStatusEdit = (scheduleId) => {
    setEditingStatusRows(prev => ({ ...prev, [scheduleId]: false }));
    setEditStatusValues(prev => ({ ...prev, [scheduleId]: undefined }));
  };

  const handleSaveEdit = (scheduleId, originalFrequency, originalStatus) => {
    const newFrequency = editValues[scheduleId];
    const newStatusValue = editStatusValues[scheduleId];
    // Map "pause" back to "paused" for API
    const newStatus = newStatusValue === "pause" ? "paused" : newStatusValue;
    
    // Always send both frequency and status - use new value if changed, otherwise use original
    const frequencyToSend = newFrequency && newFrequency !== originalFrequency 
      ? newFrequency 
      : originalFrequency;
    const statusToSend = newStatus && newStatus !== originalStatus 
      ? newStatus 
      : originalStatus;
    
    // Only update if something actually changed
    const frequencyChanged = newFrequency && newFrequency !== originalFrequency;
    const statusChanged = newStatus && newStatus !== originalStatus;
    
    if (frequencyChanged || statusChanged) {
      updateScanScheduleFrequency(
        orgCode, 
        accountCode, 
        scheduleId, 
        frequencyToSend,
        statusToSend
      );
    }
    setEditingRows(prev => ({ ...prev, [scheduleId]: false }));
    setEditingStatusRows(prev => ({ ...prev, [scheduleId]: false }));
    setEditValues(prev => ({ ...prev, [scheduleId]: undefined }));
    setEditStatusValues(prev => ({ ...prev, [scheduleId]: undefined }));
  };

  const handleFrequencyChange = (scheduleId, value) => {
    setEditValues(prev => ({ ...prev, [scheduleId]: value }));
  };

  const handleStatusChange = (scheduleId, value) => {
    setEditStatusValues(prev => ({ ...prev, [scheduleId]: value }));
  };

  const frequencyOptions = ["hourly", "daily", "weekly", "monthly"];
  const statusOptions = ["active", "pause"]; // UI shows "pause", API receives "paused"

  // Expandable row component for detailed information
  const ExpandableRow = ({ schedule }) => {
    const isExpanded = expandedRows[schedule.scheduleId];
    const isEditing = editingRows[schedule.scheduleId];
    const isEditingStatus = editingStatusRows[schedule.scheduleId];
    const currentEditValue = editValues[schedule.scheduleId];
    const currentStatusValue = editStatusValues[schedule.scheduleId];
    const originalFrequency = schedule.frequency;
    const originalStatus = schedule.status;
    // Map "paused" to "pause" for comparison
    const originalStatusDisplay = originalStatus === "paused" ? "pause" : originalStatus;
    const frequencyChanged = currentEditValue && currentEditValue !== originalFrequency;
    const statusChanged = currentStatusValue && currentStatusValue !== originalStatusDisplay;
    const hasChanged = frequencyChanged || statusChanged;
    const canEditFrequency = schedule.scheduleType === "recurring";
    const canEditStatus = true; // Status can be edited for all schedule types
    
    return (
      <>
        <TableRow>
          <TableCell>
            <IconButton
              size="small"
              onClick={() => toggleRowExpansion(schedule.scheduleId)}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {schedule.scheduleId}
            </Typography>
          </TableCell>
          <TableCell>
            <Chip
              label={schedule.scheduleType || "N/A"}
              size="small"
              color={getScheduleTypeColor(schedule.scheduleType)}
            />
          </TableCell>
          <TableCell>
            {isEditing && canEditFrequency ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={currentEditValue || ""}
                    onChange={(e) => handleFrequencyChange(schedule.scheduleId, e.target.value)}
                    disabled={isUpdatingScanSchedule}
                  >
                    {frequencyOptions.map((freq) => (
                      <MenuItem key={freq} value={freq}>
                        {freq.charAt(0).toUpperCase() + freq.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(isEditing || isEditingStatus) && (
                  <>
                    <IconButton
                      size="small"
                      color="success"
                      disabled={!hasChanged || isUpdatingScanSchedule}
                      onClick={() => handleSaveEdit(schedule.scheduleId, originalFrequency, originalStatus)}
                      sx={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: hasChanged ? '#4caf50' : '#e0e0e0',
                        color: hasChanged ? 'white' : '#9e9e9e',
                        '&:hover': {
                          backgroundColor: hasChanged ? '#45a049' : '#d0d0d0',
                        }
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={isUpdatingScanSchedule}
                      onClick={() => {
                        handleCancelEdit(schedule.scheduleId);
                        if (isEditingStatus) handleCancelStatusEdit(schedule.scheduleId);
                      }}
                      sx={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#f44336',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#da190b',
                        }
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {schedule.frequency ? schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1) : "N/A"}
                </Typography>
                {canEditFrequency && (
                  <IconButton
                    size="small"
                    onClick={() => handleEditClick(schedule.scheduleId, schedule.frequency)}
                    disabled={isUpdatingScanSchedule}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </TableCell>
          <TableCell>
            {isEditingStatus ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={currentStatusValue || ""}
                    onChange={(e) => handleStatusChange(schedule.scheduleId, e.target.value)}
                    disabled={isUpdatingScanSchedule}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!(isEditing && canEditFrequency) && (
                  <>
                    <IconButton
                      size="small"
                      color="success"
                      disabled={!hasChanged || isUpdatingScanSchedule}
                      onClick={() => handleSaveEdit(schedule.scheduleId, originalFrequency, originalStatus)}
                      sx={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: hasChanged ? '#4caf50' : '#e0e0e0',
                        color: hasChanged ? 'white' : '#9e9e9e',
                        '&:hover': {
                          backgroundColor: hasChanged ? '#45a049' : '#d0d0d0',
                        }
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={isUpdatingScanSchedule}
                      onClick={() => {
                        handleCancelStatusEdit(schedule.scheduleId);
                        if (isEditing && canEditFrequency) handleCancelEdit(schedule.scheduleId);
                      }}
                      sx={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#f44336',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#da190b',
                        }
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={schedule.status === "paused" ? "Pause" : (schedule.status || "N/A")}
                  size="small"
                  color={getStatusColor(schedule.status)}
                />
                {canEditStatus && (
                  <IconButton
                    size="small"
                    onClick={() => handleEditStatusClick(schedule.scheduleId, schedule.status)}
                    disabled={isUpdatingScanSchedule}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </TableCell>
          <TableCell>
            <Typography variant="body2">
              {formatDate(schedule.nextRunDate)}
            </Typography>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Schedule Details
                </Typography>
                <Table size="small" aria-label="schedule details">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: '30%' }}><strong>Resource Scope Type</strong></TableCell>
                      <TableCell>{schedule.resourceScopeType || "N/A"}</TableCell>
                      <TableCell sx={{ width: '30%' }}><strong>Account Number</strong></TableCell>
                      <TableCell>{schedule.accountNumber || "N/A"}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Last Run Date</strong></TableCell>
                      <TableCell>{formatDate(schedule.lastRunDate)}</TableCell>
                      <TableCell><strong>Created At</strong></TableCell>
                      <TableCell>{formatDate(schedule.createdAt)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Created By</strong></TableCell>
                      <TableCell colSpan={3}>{schedule.createdBy || "N/A"}</TableCell>
                    </TableRow>
                    {schedule.tag && schedule.tag.length > 0 && (
                      <TableRow>
                        <TableCell><strong>Tags</strong></TableCell>
                        <TableCell colSpan={3}>
                          {schedule.tag.join(", ")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        style: {
          minHeight: "80vh",
          maxHeight: "95vh",
          width: "95vw",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          Scan Schedules for Account: {accountNumber} {accountName && `- ${accountName}`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading Spinner */}
        {isGettingScanSchedules && <LoadingSpinner />}

        {/* Error Message */}
        {getScanSchedulesError && !isGettingScanSchedules && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading scan schedules: {getScanSchedulesError.message || JSON.stringify(getScanSchedulesError)}
          </Alert>
        )}

        {/* Update Success Message */}
        {updateScanScheduleMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {updateScanScheduleMessage}
          </Alert>
        )}

        {/* Update Error Message */}
        {updateScanScheduleError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error updating schedule: {updateScanScheduleError.message || JSON.stringify(updateScanScheduleError)}
          </Alert>
        )}

        {/* Update Loading Spinner */}
        {isUpdatingScanSchedule && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <LoadingSpinner />
          </Box>
        )}

        {/* Schedules Table */}
        {scanSchedules && !isGettingScanSchedules && (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader aria-label="scan schedules table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>Expand</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Schedule ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Schedule Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Frequency</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Next Run Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scanSchedules.length > 0 ? (
                  // Sort: recurring schedules first, then on_demand
                  [...scanSchedules]
                    .sort((a, b) => {
                      if (a.scheduleType === 'recurring' && b.scheduleType !== 'recurring') return -1;
                      if (a.scheduleType !== 'recurring' && b.scheduleType === 'recurring') return 1;
                      return 0;
                    })
                    .map((schedule) => (
                      <ExpandableRow key={schedule.scheduleId} schedule={schedule} />
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No scan schedules found for this account.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* No Data Message */}
        {!scanSchedules && !isGettingScanSchedules && !getScanSchedulesError && (
          <Typography variant="body1" color="textSecondary" align="center">
            No scan schedule data available for this account.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScanScheduleModal;

